import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { Controller } from "../lifecycle";

gsap.registerPlugin(ScrollTrigger);

export class ChapterMotionController implements Controller {
  private triggers: ScrollTrigger[] = [];
  private nav?: HTMLElement;
  private progress?: HTMLElement;
  private scrollFrame = 0;
  private navFloating = false;

  private readonly onScroll = (): void => {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      const next = window.scrollY > 80;
      if (next !== this.navFloating) {
        this.navFloating = next;
        this.nav?.classList.toggle("is-floating", next);
      }
      this.scrollFrame = 0;
    });
  };

  mount(root: ParentNode): void {
    this.nav = document.querySelector<HTMLElement>("[data-nav-morph]") ?? undefined;
    this.progress =
      root.querySelector<HTMLElement>("[data-chapter-progress]") ?? undefined;
    const chapters = Array.from(
      root.querySelectorAll<HTMLElement>("[data-film-chapter]")
    );
    if (chapters.length === 0) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    chapters.forEach((chapter, index) => {
      const content = chapter.querySelector<HTMLElement>(".chapter__content");
      const trigger = ScrollTrigger.create({
        trigger: chapter,
        start: "top 58%",
        end: "bottom 42%",
        onEnter: () => this.activate(chapter, index, chapters.length),
        onEnterBack: () => this.activate(chapter, index, chapters.length)
      });
      this.triggers.push(trigger);

      if (content && !reduced) {
        gsap.fromTo(
          content,
          { y: 24 },
          {
            y: 0,
            duration: 0.72,
            ease: "power3.out",
            scrollTrigger: {
              trigger: chapter,
              start: "top 72%",
              toggleActions: "play none none reverse"
            }
          }
        );
        const animationTrigger = ScrollTrigger.getAll().at(-1);
        if (animationTrigger) this.triggers.push(animationTrigger);
      }
    });

    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  }

  private activate(
    chapter: HTMLElement,
    index: number,
    total: number
  ): void {
    document
      .querySelectorAll<HTMLElement>("[data-film-chapter].is-active")
      .forEach((item) => item.classList.remove("is-active"));
    chapter.classList.add("is-active");
    if (this.progress) {
      this.progress.style.setProperty(
        "--chapter-progress",
        String((index + 1) / total)
      );
    }
  }

  destroy(): void {
    this.triggers.forEach((trigger) => trigger.kill());
    this.triggers = [];
    window.removeEventListener("scroll", this.onScroll);
    cancelAnimationFrame(this.scrollFrame);
    this.nav?.classList.remove("is-floating");
    this.nav = undefined;
    this.progress = undefined;
    this.navFloating = false;
  }
}
